import loadable from '@loadable/component';
import { type FunctionComponent, type PropsWithChildren, type ReactElement, useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { ClickableElement } from '../../../../../components/buttons';
import { ProductMatchingProducts } from '../../../../../components/product-components/product-matching-products/product-matching-products.component';
import { ProductQuestionsAndAnswers } from '../../../../../components/product-components/product-questions-and-answers/product-questions-and-answers.component';
import { StarRatingWithCountAndValue } from '../../../../../components/product-components/rating/rating.component';
import { RedirectWithStatus } from '../../../../../components/redirect-w-status';
import ChevronDownIcon from '../../../../../components/svgs/chevron-down.svg';
import ChevronUpIcon from '../../../../../components/svgs/chevron-up.svg';
import { FEATURE_FLAGS } from '../../../../../constants/general';
import {
	MATCHING_PRODUCTS_SECTION_ID,
	OVERVIEW_SECTION_ID,
	PRODUCT_PARTS_SECTION_ID,
	PRODUCT_QA_SECTION_ID,
	PdpDyPlacementEnum,
	REVIEWS_SECTION_ID
} from '../../../../../constants/product';
import { ProductStickyNavContext } from '../../../../../contexts/product-sticky-nav/product-sticky-nav.context';
import { ReviewsContextProvider } from '../../../../../contexts/review/reviews.context';
import { buildGTMReviewsExpanded, buildGTMQAExpanded } from '../../../../../helpers/analytics/gtm/product-event-builders';
import { buildContainerPageUrl } from '../../../../../helpers/product-helper/reviews-qa.helper';
import { useTrackEvent } from '../../../../../hooks/analytics/analytics.hooks';
import { useFeature } from '../../../../../hooks/features/features.hooks';
import { useRouterLocation } from '../../../../../hooks/routing/location.hook';
import { type PdpProductFamily } from '../../../../../types/product.types';
import { ProductOverview } from '../product-overview/product-overview.component';

const TITLE_QA = 'Product Q&A';
const TITLE_REVIEWS = 'Reviews';
const TITLE_MATCHING_PRODUCTS = 'Matching Products';
const TITLE_DETAILS = 'Details';
const TITLE_PARTS = 'Parts';

const LoadableProductReviews = loadable(
	() =>
		import(
			/* webpackChunkName: "ProductReviews" */ '../../../../../components/product-components/product-reviews/product-reviews.component'
		),
	{
		resolveComponent: ({ ProductReviews }) => ProductReviews
	}
);

const LoadableProductParts = loadable(
	() =>
		import(/* webpackChunkName: "ProductParts" */ '../../../../../components/product-components/product-parts/product-parts.component'),
	{
		resolveComponent: ({ ProductParts }) => ProductParts
	}
);

const LoadableQuestionsAnswersContainer = loadable(
	() =>
		import(
			/* webpackChunkName: "QuestionsAnswersContainer" */ '../../../../../components/product-components/product-questions-and-answers/questions-answers-container/questions-answers-container.component'
		),
	{
		resolveComponent: ({ QuestionsAnswersContainer }) => QuestionsAnswersContainer
	}
);

type SectionHeaderProps = {
	title: string;
	isOpen: boolean | undefined;
	onClick: () => void;
	headerContent?: ReactElement;
};

const SectionHeader: FunctionComponent<PropsWithChildren<SectionHeaderProps>> = ({ title, isOpen, onClick, headerContent, children }) => {
	const trackEvent = useTrackEvent();
	useEffect(() => {
		if (isOpen) {
			if (title === TITLE_REVIEWS) {
				trackEvent(buildGTMReviewsExpanded());
			} else if (title === TITLE_QA) {
				trackEvent(buildGTMQAExpanded());
			}
		}
	}, [isOpen]);

	return (
		<>
			<ClickableElement onClick={onClick} ariaLabel={`${title} header`} className="hover-bg-theme-grey-lighter">
				<div className="flex flex-row">
					<div className="flex flex-column w-100">
						<div className="flex pl3">
							<h3 className="f3">{title}</h3>
							{headerContent && <div className="flex flex-column justify-center pl3 pt1">{headerContent}</div>}
						</div>
					</div>
					<div className="flex flex-column justify-center pr3 f3">{isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
				</div>
			</ClickableElement>
			<div className={`pb5 ph3 bt b--theme-grey-light ${isOpen ? '' : 'dn'} `}>{children}</div>
		</>
	);
};

type PdpSectionContainerProps = {
	title: string;
	id: string;
	className?: string;
	headerContent?: ReactElement;
	startOpen?: boolean;
	showHeader?: boolean;
};

const SectionContainer: FunctionComponent<PropsWithChildren<PdpSectionContainerProps>> = ({
	title,
	id,
	className,
	startOpen,
	headerContent,
	showHeader = true,
	children
}) => {
	const [isOpen, setIsOpen] = useState(startOpen);
	const { ref: sectionRef, inView } = useInView({ rootMargin: '-5% 0% -85% 0%' });
	const { setCurrentActiveSection, sectionToOpen, setSectionToOpen } = useContext(ProductStickyNavContext);

	useEffect(() => {
		if (inView) {
			setCurrentActiveSection(id);
		}
	}, [inView]);

	useEffect(() => {
		if (sectionToOpen === id) {
			setIsOpen(true);
			setSectionToOpen('');
		}
	}, [sectionToOpen]);

	const handleHeaderClick = () => {
		setIsOpen(!isOpen);
	};

	return (
		<section
			id={id}
			className={`mv2 ba br1 b--theme-grey-light ${className}`}
			data-testid={`pdp-${title.toLowerCase}-section`}
			ref={sectionRef}>
			{showHeader ? (
				<SectionHeader title={title} isOpen={isOpen} onClick={handleHeaderClick} headerContent={headerContent}>
					<>{isOpen && children}</>
				</SectionHeader>
			) : (
				<>{isOpen && children}</>
			)}
		</section>
	);
};

export type ProductDetailsSectionProps = {
	product: PdpProductFamily;
	familyId: number;
	currentDyPlacement?: PdpDyPlacementEnum;
};

export const ProductDetailsSection: FunctionComponent<ProductDetailsSectionProps> = ({ product, familyId, currentDyPlacement }) => {
	const { ref: detailsRef } = useInView({ triggerOnce: true });
	const { ref: replacementPartsRef, inView: replacementPartsInView } = useInView({ triggerOnce: true });
	const { ref: matchingProductsRef, inView: matchingProductsInView } = useInView({ triggerOnce: true });

	const isDyVariationTwoActive = currentDyPlacement && currentDyPlacement === PdpDyPlacementEnum.VARIATION_TWO;

	// If reviews=true is in the URL, redirect to the container page.
	const { search } = useRouterLocation();
	const params = new URLSearchParams(search);
	const shouldRedirectToContainer = params.get('reviews') === 'true';
	const campaignId = params.get('campaign');

	const bazaarVoiceAPIActive = useFeature(FEATURE_FLAGS.BAZAARVOICE_QA_API);

	return !shouldRedirectToContainer ? (
		<section data-testid="product-details-section-sticky-nav" className="w-100">
			<div ref={detailsRef}>
				<SectionContainer title={TITLE_DETAILS} id={OVERVIEW_SECTION_ID} startOpen={true}>
					<ProductOverview product={product} />
				</SectionContainer>
			</div>
			{/* Recs */}
			{isDyVariationTwoActive && <div id="dy-recs-product-page-2" />}
			<div>
				<SectionContainer
					title={TITLE_REVIEWS}
					id={REVIEWS_SECTION_ID}
					headerContent={
						<StarRatingWithCountAndValue
							reviewCount={product.reviewSummary.reviewCount}
							ratingValue={product.reviewSummary.ratingValue}
						/>
					}>
					<ReviewsContextProvider familyId={familyId}>
						<LoadableProductReviews />
					</ReviewsContextProvider>
				</SectionContainer>
			</div>
			<div>
				<SectionContainer title={TITLE_QA} id={PRODUCT_QA_SECTION_ID}>
					{bazaarVoiceAPIActive ? <LoadableQuestionsAnswersContainer /> : <ProductQuestionsAndAnswers id={product.id} />}
				</SectionContainer>
			</div>
			<div ref={matchingProductsRef}>
				<SectionContainer title={TITLE_MATCHING_PRODUCTS} id={MATCHING_PRODUCTS_SECTION_ID} startOpen={true}>
					{matchingProductsInView && <ProductMatchingProducts product={product} />}
				</SectionContainer>
			</div>
			<div ref={replacementPartsRef}>
				{product.hasReplacementParts && (
					<SectionContainer title={TITLE_PARTS} id={PRODUCT_PARTS_SECTION_ID} startOpen={true}>
						{replacementPartsInView && <LoadableProductParts familyId={familyId} />}
					</SectionContainer>
				)}
			</div>
		</section>
	) : (
		<RedirectWithStatus to={buildContainerPageUrl({ campaignId: campaignId || '', productId: `cp-${String(product.id)}` })} />
	);
};
